import { normalizeCurrencyCode } from '@/src/helpers';
import { useTokens, useAccountLines, useAccountInfo } from '@/src/hooks';
import { Token, TokenId } from '@/src/models';
import { getNetworkTokenConfig } from '@/src/constants';
import {
  Flex,
  FormControl,
  FormField,
  FormItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
  TokenIcon,
} from '@/src/ui-components';
import { useFormContext } from 'react-hook-form';
import { TradingFormValues } from '../../trading.types';
import { useMemo } from 'react';
import { useNetwork, useAuth } from '@/src/contexts';

type TradingPairOption = {
  baseTokenId: string;
  quoteTokenId: string;
  label: string;
  baseToken: { code: string; icon: string };
  quoteToken: { code: string; icon: string };
};

interface AccountLine {
  currency: string;
  account: string;
  balance: string;
}

export const TradingPair = () => {
  const { control, setValue } = useFormContext<TradingFormValues>();
  const tokens = useTokens();
  const { network } = useNetwork();
  const { user } = useAuth();

  // Get XRPL wallet data
  const xrplAddress = user?.xrp_address;
  const { data: accountInfo } = useAccountInfo({
    account: xrplAddress ?? '',
  });
  const { data: accountLines } = useAccountLines({
    account: xrplAddress ?? '',
  });

  // Get trading pair configuration from token_config.json
  const networkConfig = getNetworkTokenConfig(network);
  const allowedBaseTokenIds = networkConfig?.tradingPairs.baseTokenIds || [];
  const allowedQuoteTokenIds = networkConfig?.tradingPairs.quoteTokenIds || [];

  // Extract wallet tokens and merge with configured base tokens
  const baseTokenOptions = useMemo(() => {
    // Start with configured base tokens
    const configuredBaseTokens = tokens.filter((token) =>
      allowedBaseTokenIds.includes(token.id)
    );

    // Dynamically discovered wallet tokens (not in TOKEN_MAP)
    const dynamicWalletTokens: Token[] = [];

    // Add XRP if wallet has balance
    if (accountInfo?.account_data?.Balance) {
      const xrpToken = tokens.find((t) => t.code === 'XRP');
      if (xrpToken && !allowedBaseTokenIds.includes(xrpToken.id) && !allowedQuoteTokenIds.includes(xrpToken.id)) {
        dynamicWalletTokens.push(xrpToken);
      }
    }

    // Add issued currencies from trust lines
    if (accountLines?.lines) {
      (accountLines.lines as AccountLine[]).forEach((line) => {
        const currencyCode = line.currency;
        const issuerAddress = line.account.trim();

        // Try to find matching token in TOKEN_MAP
        const token = tokens.find((t) =>
          t.code === currencyCode && t.issuer === issuerAddress
        );

        if (token) {
          // Token exists in TOKEN_MAP
          // Add if not already in configured base/quote tokens
          if (!allowedBaseTokenIds.includes(token.id) && !allowedQuoteTokenIds.includes(token.id)) {
            dynamicWalletTokens.push(token);
          }
        } else {
          // Token NOT in TOKEN_MAP - dynamically create it
          const normalizedCode = normalizeCurrencyCode(currencyCode);
          const dynamicTokenId = `${normalizedCode.toLowerCase()}-${issuerAddress.substring(0, 8)}-dynamic` as TokenId;

          const dynamicToken: Token = {
            id: dynamicTokenId,
            coinGeckoId: 'ripple', // Default fallback
            code: currencyCode,
            issuer: issuerAddress,
            description: normalizedCode,
            icon: '/placeholder-token.svg'
          };

          dynamicWalletTokens.push(dynamicToken);
        }
      });
    }

    return [...configuredBaseTokens, ...dynamicWalletTokens];
  }, [tokens, allowedBaseTokenIds, allowedQuoteTokenIds, accountInfo, accountLines, xrplAddress]);

  const quoteTokenOptions = useMemo(
    () => tokens.filter((token) => allowedQuoteTokenIds.includes(token.id)),
    [tokens, allowedQuoteTokenIds]
  );

  // Generate all possible trading pairs
  const tradingPairs = useMemo(() => {
    const pairs: TradingPairOption[] = [];
    baseTokenOptions.forEach((baseToken) => {
      quoteTokenOptions.forEach((quoteToken) => {
        pairs.push({
          baseTokenId: baseToken.id,
          quoteTokenId: quoteToken.id,
          label: `${normalizeCurrencyCode(baseToken.code)} / ${normalizeCurrencyCode(quoteToken.code)}`,
          baseToken: {
            code: baseToken.code,
            icon: baseToken.icon || '/placeholder-token.svg',
          },
          quoteToken: {
            code: quoteToken.code,
            icon: quoteToken.icon || '/placeholder-token.svg',
          },
        });
      });
    });
    return pairs;
  }, [baseTokenOptions, quoteTokenOptions]);

  return (
    <FormField
      control={control}
      name="baseTokenId"
      render={({ field }) => {
        // Find current pair based on both baseTokenId and quoteTokenId
        const currentPairValue = `${field.value}`;

        return (
          <FormItem>
            <Flex align="center" justify="start">
              <Select
                onValueChange={(value) => {
                  // Parse the selected pair and update both fields
                  const pair = tradingPairs.find((p) => p.baseTokenId === value);
                  if (pair) {
                    setValue('baseTokenId', pair.baseTokenId as TokenId);
                    setValue('quoteTokenId', pair.quoteTokenId as TokenId);
                  }
                }}
                value={currentPairValue}
                defaultValue={currentPairValue}
              >
                <FormControl>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Select trading pair" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-w-md">
                  {tradingPairs.map((pair) => (
                    <SelectItem key={pair.baseTokenId} value={pair.baseTokenId}>
                      <Flex align="center" gap="2">
                        <TokenIcon
                          src={pair.baseToken.icon}
                          alt={pair.baseToken.code}
                          width={20}
                          height={20}
                        />
                        <Text className="font-mono">
                          {normalizeCurrencyCode(pair.baseToken.code)}
                        </Text>
                        <Text className="text-muted-foreground">/</Text>
                        <TokenIcon
                          src={pair.quoteToken.icon}
                          alt={pair.quoteToken.code}
                          width={20}
                          height={20}
                        />
                        <Text className="font-mono">
                          {normalizeCurrencyCode(pair.quoteToken.code)}
                        </Text>
                      </Flex>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Flex>
          </FormItem>
        );
      }}
    />
  );
};
